/**
 * Settings.jsx - Embedded admin UI for Quick Urgency Timer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSessionToken as getShopifySessionToken } from "@shopify/app-bridge-utils/utilities/session-token";
import {
  Badge,
  BlockStack,
  Button,
  Card,
  Checkbox,
  Divider,
  FormLayout,
  Frame,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  RadioButton,
  Select,
  Spinner,
  Text,
  TextField,
  Toast,
} from "@shopify/polaris";
import TimerPreview from "./TimerPreview";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:3000");

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "timers", label: "Timers" },
  { id: "builder", label: "Builder" },
];

const BUILDER_STEPS = [
  { id: "template", label: "Template" },
  { id: "settings", label: "Settings" },
  { id: "content", label: "Content" },
  { id: "design", label: "Design" },
];

const DEFAULT_DESIGN = {
  backgroundMode: "solid",
  backgroundColor: "#fff4f0",
  gradientStartColor: "#fff4f0",
  gradientEndColor: "#fff4f0",
  gradientAngle: 90,
  textColor: "#202223",
  accentColor: "#bf0711",
  eyebrowColor: "#bf0711",
  titleColor: "#202223",
  subtitleColor: "#202223",
  timerColor: "#202223",
  ctaBackgroundColor: "#bf0711",
  ctaTextColor: "#ffffff",
  brandingBackgroundColor: "#bf0711",
  brandingTextColor: "#ffffff",
  fontSize: 20,
  eyebrowFontSize: 14,
  titleFontSize: 22,
  subtitleFontSize: 16,
  timerFontSize: 26,
  ctaFontSize: 14,
  borderRadius: 8,
  blockShape: "square",
  blockBorderRadius: 8,
  timeBoxBackgroundColor: "#ffffff",
  timeBoxTextColor: "#202223",
  timeBoxBorderColor: "#ffffff",
  timeBoxBorderStyle: "none",
  timeBoxBorderWidth: 0,
  flagSideTailLength: 0,
  showDays: true,
  showHours: true,
  showMinutes: true,
  showSeconds: true,
  showTimeLabels: true,
  layout: "vertical",
  displayStyle: "blocks",
  paddingTop: 16,
  paddingBottom: 16,
  customCss: "",
};

const TEMPLATE_THEMES = [
  {
    id: "coral",
    name: "Coral",
    swatch: "#ef2e2e",
    design: {
      backgroundMode: "gradient",
      backgroundColor: "#ff4b55",
      gradientStartColor: "#ef5350",
      gradientEndColor: "#ff7a00",
      gradientAngle: 90,
      accentColor: "#ef2e2e",
      ctaBackgroundColor: "#111111",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
    },
  },
  {
    id: "maroon",
    name: "Maroon",
    swatch: "#320000",
    design: {
      backgroundMode: "solid",
      backgroundColor: "#320000",
      gradientStartColor: "#320000",
      gradientEndColor: "#320000",
      gradientAngle: 90,
      textColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#e9d7d7",
      timerColor: "#ffffff",
      accentColor: "#ffffff",
      ctaBackgroundColor: "#f45b5b",
      ctaTextColor: "#ffffff",
      timeBoxBackgroundColor: "transparent",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 3,
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
    },
  },
  {
    id: "blue",
    name: "Blue",
    swatch: "#1d6fd7",
    design: {
      backgroundMode: "gradient",
      backgroundColor: "#1d6fd7",
      gradientStartColor: "#1d6fd7",
      gradientEndColor: "#173a8a",
      gradientAngle: 90,
      textColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#eaf2ff",
      timerColor: "#ffffff",
      accentColor: "#ffffff",
      ctaBackgroundColor: "#ffffff",
      ctaTextColor: "#173a8a",
      timeBoxBackgroundColor: "rgba(255,255,255,0.12)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 1,
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
    },
  },
  {
    id: "green",
    name: "Green",
    swatch: "#008060",
    design: {
      backgroundMode: "gradient",
      backgroundColor: "#008060",
      gradientStartColor: "#008060",
      gradientEndColor: "#14532d",
      gradientAngle: 90,
      textColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#dcfce7",
      timerColor: "#ffffff",
      accentColor: "#ffffff",
      ctaBackgroundColor: "#ffffff",
      ctaTextColor: "#14532d",
      timeBoxBackgroundColor: "rgba(255,255,255,0.14)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "dashed",
      timeBoxBorderWidth: 2,
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
    },
  },
  {
    id: "magenta",
    name: "Magenta",
    swatch: "#d5169e",
    design: {
      backgroundMode: "gradient",
      backgroundColor: "#d5169e",
      gradientStartColor: "#d5169e",
      gradientEndColor: "#7657f4",
      gradientAngle: 90,
      textColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#fce7f3",
      timerColor: "#ffffff",
      accentColor: "#ffffff",
      ctaBackgroundColor: "#111111",
      ctaTextColor: "#ffffff",
      timeBoxBackgroundColor: "rgba(255,255,255,0.13)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 2,
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
    },
  },
];

const DEFAULT_CONTENT = {
  eyebrow: "Limited time",
  title: "Sale ends soon",
  subtitle: "Complete your purchase before the offer expires.",
  timerLabel: "Ends in",
  ctaLabel: "Shop now",
  ctaUrl: "",
  ctaBehavior: "visible",
};

function getDefaultBuilderTimer() {
  return {
    id: null,
    name: "New timer",
    status: "draft",
    timerEnabled: true,
    startTrigger: "fixed",
    durationMinutes: 90,
    startAt: "",
    endAt: "2099-12-31T23:59",
    pageTarget: "product",
    productTargets: [],
    collectionTargets: [],
    placementTargets: ["top"],
    content: { ...DEFAULT_CONTENT },
    design: { ...DEFAULT_DESIGN },
  };
}

const TIMER_TEMPLATES = [
  {
    id: "flash-sale-banner",
    name: "Flash sale banner",
    description: "A bold full-width countdown for urgent product and collection promos.",
    tone: "coral",
    pageTarget: "product",
    placementTargets: ["top"],
    content: {
      eyebrow: "Flash sale ⚡",
      title: "SALE ENDS IN",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "",
      ctaUrl: "",
      ctaBehavior: "hidden",
    },
    design: {
      backgroundColor: "#ff404c",
      textColor: "#ffffff",
      accentColor: "#ffd52e",
      eyebrowColor: "#9b1b08",
      titleColor: "#ffffff",
      subtitleColor: "#ffffff",
      timerColor: "#b91520",
      ctaBackgroundColor: "#ffffff",
      ctaTextColor: "#ff404c",
      brandingBackgroundColor: "#d64022",
      brandingTextColor: "#ffffff",
      fontSize: 22,
      eyebrowFontSize: 14,
      titleFontSize: 24,
      subtitleFontSize: 16,
      timerFontSize: 28,
      ctaFontSize: 14,
      borderRadius: 12,
      blockShape: "square",
      blockBorderRadius: 8,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      layout: "vertical",
      displayStyle: "blocks",
      paddingTop: 18,
      paddingBottom: 18,
      customCss: "",
    },
  },
  {
    id: "shipping-cutoff",
    name: "Shipping cutoff bar",
    description: "A clean deadline timer for cart or product shipping promises.",
    tone: "blue",
    pageTarget: "cart",
    placementTargets: ["cart-footer"],
    content: {
      eyebrow: "Shipping deadline",
      title: "Order before dispatch closes",
      subtitle: "Complete checkout to keep today's shipping window.",
      timerLabel: "Ships in",
      ctaLabel: "Checkout",
      ctaUrl: "/cart",
      ctaBehavior: "visible",
    },
    design: {
      backgroundColor: "#eef6ff",
      textColor: "#122033",
      accentColor: "#1d6fd7",
      eyebrowColor: "#1d6fd7",
      titleColor: "#122033",
      subtitleColor: "#122033",
      timerColor: "#122033",
      ctaBackgroundColor: "#1d6fd7",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#1d6fd7",
      brandingTextColor: "#ffffff",
      fontSize: 20,
      eyebrowFontSize: 13,
      titleFontSize: 20,
      subtitleFontSize: 15,
      timerFontSize: 24,
      ctaFontSize: 14,
      borderRadius: 10,
      blockShape: "square",
      blockBorderRadius: 10,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      layout: "horizontal",
      displayStyle: "compact",
      paddingTop: 14,
      paddingBottom: 14,
      customCss: "",
    },
  },
  {
    id: "product-countdown",
    name: "Product page countdown",
    description: "A refined product detail timer for above-buy-button placement.",
    tone: "green",
    pageTarget: "product",
    placementTargets: ["above-buy-button"],
    content: {
      eyebrow: "Limited inventory",
      title: "Reserve this offer",
      subtitle: "The countdown keeps urgency close to the purchase action.",
      timerLabel: "Offer ends",
      ctaLabel: "Add to cart",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      backgroundColor: "#ecf8f1",
      textColor: "#14251d",
      accentColor: "#008060",
      eyebrowColor: "#008060",
      titleColor: "#14251d",
      subtitleColor: "#14251d",
      timerColor: "#14251d",
      ctaBackgroundColor: "#008060",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#008060",
      brandingTextColor: "#ffffff",
      fontSize: 19,
      eyebrowFontSize: 13,
      titleFontSize: 20,
      subtitleFontSize: 15,
      timerFontSize: 25,
      ctaFontSize: 14,
      borderRadius: 14,
      blockShape: "circle",
      blockBorderRadius: 999,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      layout: "vertical",
      displayStyle: "blocks",
      paddingTop: 18,
      paddingBottom: 18,
      customCss: "",
    },
  },
  {
    id: "minimal-announcement",
    name: "Minimal announcement",
    description: "A quieter announcement-style timer for broad storefront messaging.",
    tone: "slate",
    pageTarget: "all",
    placementTargets: ["sticky-top"],
    content: {
      eyebrow: "Today only",
      title: "Limited-time storefront offer",
      subtitle: "A compact announcement that stays readable across pages.",
      timerLabel: "Time left",
      ctaLabel: "View offer",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      backgroundColor: "#f4f6f8",
      textColor: "#17212b",
      accentColor: "#56606b",
      eyebrowColor: "#56606b",
      titleColor: "#17212b",
      subtitleColor: "#17212b",
      timerColor: "#17212b",
      ctaBackgroundColor: "#56606b",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#56606b",
      brandingTextColor: "#ffffff",
      fontSize: 18,
      eyebrowFontSize: 12,
      titleFontSize: 18,
      subtitleFontSize: 14,
      timerFontSize: 24,
      ctaFontSize: 13,
      borderRadius: 8,
      blockShape: "square",
      blockBorderRadius: 8,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      layout: "horizontal",
      displayStyle: "compact",
      paddingTop: 12,
      paddingBottom: 12,
      customCss: "",
    },
  },
  {
    id: "vip-drop",
    name: "VIP drop timer",
    description: "A dark, premium launch countdown for limited product drops.",
    tone: "midnight",
    pageTarget: "product",
    placementTargets: ["above-buy-button"],
    content: {
      eyebrow: "VIP access",
      title: "Drop closes soon",
      subtitle: "Reserve inventory before this private window ends.",
      timerLabel: "Closes in",
      ctaLabel: "Reserve now",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundColor: "#121826",
      textColor: "#ffffff",
      accentColor: "#f5c542",
      eyebrowColor: "#f5c542",
      titleColor: "#ffffff",
      subtitleColor: "#d8dee9",
      timerColor: "#121826",
      ctaBackgroundColor: "#f5c542",
      ctaTextColor: "#121826",
      brandingBackgroundColor: "#384152",
      brandingTextColor: "#ffffff",
      layout: "vertical",
      displayStyle: "blocks",
      borderRadius: 14,
      blockShape: "circle",
      blockBorderRadius: 999,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
    },
  },
  {
    id: "free-shipping-nudge",
    name: "Free shipping nudge",
    description: "A compact bar for shipping thresholds and checkout nudges.",
    tone: "peach",
    pageTarget: "cart",
    placementTargets: ["cart-footer"],
    content: {
      eyebrow: "",
      title: "Free shipping on orders over $75",
      subtitle: "",
      timerLabel: "Ends in",
      ctaLabel: "Checkout",
      ctaUrl: "/cart",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundColor: "#fff4e8",
      textColor: "#1f2933",
      accentColor: "#f05a28",
      eyebrowColor: "#f05a28",
      titleColor: "#1f2933",
      subtitleColor: "#1f2933",
      timerColor: "#f05a28",
      ctaBackgroundColor: "#f05a28",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#f05a28",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "compact",
      borderRadius: 10,
      blockShape: "square",
      blockBorderRadius: 10,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 12,
      paddingBottom: 12,
    },
  },
  {
    id: "green-product-offer",
    name: "Botanical product offer",
    description: "A soft product-page countdown suited for beauty and wellness stores.",
    tone: "botanical",
    pageTarget: "product",
    placementTargets: ["above-buy-button"],
    content: {
      eyebrow: "",
      title: "Hurry! Offer ends in",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "Add to cart",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundColor: "#effaf3",
      textColor: "#14573a",
      accentColor: "#0f8f5a",
      eyebrowColor: "#0f8f5a",
      titleColor: "#14573a",
      subtitleColor: "#14573a",
      timerColor: "#0f8f5a",
      ctaBackgroundColor: "#0f8f5a",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#0f8f5a",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 12,
      blockShape: "circle",
      blockBorderRadius: 999,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
    },
  },
  {
    id: "top-right-flash-flag",
    name: "Top-right flash flag",
    description: "A compact image-corner flag for product cards with a short urgency label and countdown.",
    tone: "green",
    templateCategory: "product-card-flags",
    previewPresentation: "product-card-flag",
    pageTarget: "product",
    placementTargets: ["above-buy-button"],
    content: {
      eyebrow: "Flash sale",
      title: "Ends in",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "",
      ctaUrl: "",
      ctaBehavior: "hidden",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#0e8c67",
      gradientStartColor: "#0e8c67",
      gradientEndColor: "#0e8c67",
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#d5fff0",
      titleColor: "#ffffff",
      subtitleColor: "#d5fff0",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#ffffff",
      ctaTextColor: "#0e8c67",
      brandingBackgroundColor: "#0e8c67",
      brandingTextColor: "#ffffff",
      layout: "vertical",
      displayStyle: "compact",
      borderRadius: 10,
      fontSize: 15,
      eyebrowFontSize: 10,
      titleFontSize: 11,
      subtitleFontSize: 12,
      timerFontSize: 20,
      timeBoxBackgroundColor: "transparent",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "transparent",
      timeBoxBorderStyle: "none",
      timeBoxBorderWidth: 0,
      flagSideTailLength: 10,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  {
    id: "top-right-ends-tonight-flag",
    name: "Top-right ends tonight",
    description: "A bolder product-card corner flag for end-of-day urgency on featured items.",
    tone: "green",
    templateCategory: "product-card-flags",
    previewPresentation: "product-card-flag",
    pageTarget: "product",
    placementTargets: ["above-buy-button"],
    content: {
      eyebrow: "Ends tonight",
      title: "Offer closes in",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "",
      ctaUrl: "",
      ctaBehavior: "hidden",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#0b8e67",
      gradientStartColor: "#0b8e67",
      gradientEndColor: "#0b8e67",
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#ffffff",
      titleColor: "#d6fff1",
      subtitleColor: "#d6fff1",
      timerColor: "#ffffff",
      layout: "vertical",
      displayStyle: "compact",
      borderRadius: 10,
      fontSize: 15,
      eyebrowFontSize: 10,
      titleFontSize: 10,
      subtitleFontSize: 12,
      timerFontSize: 20,
      timeBoxBackgroundColor: "transparent",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 1,
      flagSideTailLength: 14,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  {
    id: "top-right-member-perk-flag",
    name: "Top-right member perk",
    description: "A cleaner loyalty-style card flag that keeps the image mostly untouched while signaling a timed member offer.",
    tone: "green",
    templateCategory: "product-card-flags",
    previewPresentation: "product-card-flag",
    pageTarget: "product",
    placementTargets: ["above-buy-button"],
    content: {
      eyebrow: "Member perk",
      title: "Savings expire in",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "",
      ctaUrl: "",
      ctaBehavior: "hidden",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#0f8a62",
      gradientStartColor: "#0f8a62",
      gradientEndColor: "#0f8a62",
      textColor: "#ffffff",
      accentColor: "#d8fff2",
      eyebrowColor: "#d8fff2",
      titleColor: "#ffffff",
      subtitleColor: "#d8fff2",
      timerColor: "#ffffff",
      layout: "vertical",
      displayStyle: "compact",
      borderRadius: 10,
      fontSize: 15,
      eyebrowFontSize: 10,
      titleFontSize: 10,
      subtitleFontSize: 12,
      timerFontSize: 19,
      timeBoxBackgroundColor: "transparent",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "rgba(255,255,255,0.75)",
      timeBoxBorderStyle: "none",
      timeBoxBorderWidth: 0,
      flagSideTailLength: 12,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  {
    id: "top-strip-announcement",
    name: "Top strip announcement",
    description: "A full-width top strip with a centered flash-sale countdown.",
    tone: "coral",
    pageTarget: "all",
    placementTargets: ["sticky-top"],
    content: {
      eyebrow: "⚡ FLASH SALE!",
      title: "Ends in",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "Shop now",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "gradient",
      backgroundColor: "#ef5350",
      gradientStartColor: "#ef5350",
      gradientEndColor: "#ff7a00",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#ffffff",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#111111",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "custom",
      blockBorderRadius: 8,
      timeBoxBackgroundColor: "rgba(255,255,255,0.16)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "dashed",
      timeBoxBorderWidth: 2,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 16,
      paddingBottom: 16,
    },
  },
  {
    id: "bottom-strip-checkout",
    name: "Bottom strip checkout nudge",
    description: "A sticky-bottom checkout strip with outlined boxes and a strong CTA.",
    tone: "midnight",
    pageTarget: "cart",
    placementTargets: ["sticky-bottom"],
    content: {
      eyebrow: "",
      title: "Final moments to checkout",
      subtitle: "Sale ends in:",
      timerLabel: "",
      ctaLabel: "Checkout",
      ctaUrl: "/cart",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#320000",
      gradientStartColor: "#320000",
      gradientEndColor: "#320000",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#ffd54a",
      titleColor: "#ffffff",
      subtitleColor: "#e9d7d7",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#f45b5b",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#bf0711",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "custom",
      blockBorderRadius: 8,
      timeBoxBackgroundColor: "transparent",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 2,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      paddingTop: 18,
      paddingBottom: 18,
    },
  },
  {
    id: "top-strip-vip-launch",
    name: "Top strip VIP launch",
    description: "A polished sticky-top launch bar with gold accents and compact circular boxes.",
    tone: "midnight",
    pageTarget: "all",
    placementTargets: ["sticky-top"],
    content: {
      eyebrow: "VIP drop",
      title: "Early access closes",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "Shop drop",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#111827",
      gradientStartColor: "#111827",
      gradientEndColor: "#111827",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#f6c453",
      eyebrowColor: "#f6c453",
      titleColor: "#ffffff",
      subtitleColor: "#d1d5db",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#f6c453",
      ctaTextColor: "#111827",
      brandingBackgroundColor: "#374151",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "circle",
      blockBorderRadius: 999,
      timeBoxBackgroundColor: "rgba(255,255,255,0.08)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#f6c453",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 2,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      paddingTop: 14,
      paddingBottom: 14,
    },
  },
  {
    id: "top-strip-free-shipping",
    name: "Top strip free shipping",
    description: "A bright sticky-top shipping deadline bar for all-page promotions.",
    tone: "green",
    pageTarget: "all",
    placementTargets: ["sticky-top"],
    content: {
      eyebrow: "Today only",
      title: "Free shipping window",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "Shop now",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "gradient",
      backgroundColor: "#0f8f5a",
      gradientStartColor: "#0f8f5a",
      gradientEndColor: "#14b8a6",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#eafff7",
      titleColor: "#ffffff",
      subtitleColor: "#eafff7",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#ffffff",
      ctaTextColor: "#0f6f4b",
      brandingBackgroundColor: "#0f6f4b",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "custom",
      blockBorderRadius: 10,
      timeBoxBackgroundColor: "rgba(255,255,255,0.14)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "rgba(255,255,255,0.72)",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 1,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 14,
      paddingBottom: 14,
    },
  },
  {
    id: "bottom-strip-last-call",
    name: "Bottom strip last call",
    description: "A high-contrast sticky-bottom cart saver with compact countdown boxes.",
    tone: "coral",
    pageTarget: "cart",
    placementTargets: ["sticky-bottom"],
    content: {
      eyebrow: "Last call",
      title: "Your offer is almost gone",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "Complete order",
      ctaUrl: "/cart",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "gradient",
      backgroundColor: "#dc2626",
      gradientStartColor: "#dc2626",
      gradientEndColor: "#7f1d1d",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#fee2e2",
      titleColor: "#ffffff",
      subtitleColor: "#fee2e2",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#ffffff",
      ctaTextColor: "#991b1b",
      brandingBackgroundColor: "#991b1b",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "custom",
      blockBorderRadius: 6,
      timeBoxBackgroundColor: "rgba(255,255,255,0.12)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "dashed",
      timeBoxBorderWidth: 2,
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: false,
      paddingTop: 16,
      paddingBottom: 16,
    },
  },
  {
    id: "bottom-strip-member-perk",
    name: "Bottom strip member perk",
    description: "A calmer sticky-bottom member offer bar with soft boxed timer values.",
    tone: "blue",
    pageTarget: "all",
    placementTargets: ["sticky-bottom"],
    content: {
      eyebrow: "Member perk",
      title: "Extra savings expire soon",
      subtitle: "",
      timerLabel: "",
      ctaLabel: "Claim offer",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#eef6ff",
      gradientStartColor: "#eef6ff",
      gradientEndColor: "#eef6ff",
      gradientAngle: 90,
      textColor: "#122033",
      accentColor: "#1d6fd7",
      eyebrowColor: "#1d6fd7",
      titleColor: "#122033",
      subtitleColor: "#334155",
      timerColor: "#122033",
      ctaBackgroundColor: "#1d6fd7",
      ctaTextColor: "#ffffff",
      brandingBackgroundColor: "#1d6fd7",
      brandingTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "custom",
      blockBorderRadius: 8,
      timeBoxBackgroundColor: "#ffffff",
      timeBoxTextColor: "#122033",
      timeBoxBorderColor: "#b9d9ff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 1,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      paddingTop: 16,
      paddingBottom: 16,
    },
  },
  {
    id: "maroon-circle-strip",
    name: "Maroon circle strip",
    description: "A premium top or embedded bar with circular timer boxes.",
    tone: "midnight",
    pageTarget: "all",
    placementTargets: ["sticky-top"],
    content: {
      eyebrow: "",
      title: "Hurry up!",
      subtitle: "Sale ends in:",
      timerLabel: "",
      ctaLabel: "Shop now!",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "solid",
      backgroundColor: "#320000",
      gradientStartColor: "#320000",
      gradientEndColor: "#320000",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#e9d7d7",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#f45b5b",
      ctaTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "circle",
      blockBorderRadius: 999,
      timeBoxBackgroundColor: "transparent",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "solid",
      timeBoxBorderWidth: 3,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
  {
    id: "orange-dashed-strip",
    name: "Orange dashed strip",
    description: "A gradient urgency strip with dashed boxes and a dark CTA.",
    tone: "coral",
    pageTarget: "all",
    placementTargets: ["sticky-top"],
    content: {
      eyebrow: "",
      title: "Hurry up!",
      subtitle: "Sale ends in:",
      timerLabel: "",
      ctaLabel: "Shop now!",
      ctaUrl: "",
      ctaBehavior: "visible",
    },
    design: {
      ...DEFAULT_DESIGN,
      backgroundMode: "gradient",
      backgroundColor: "#ef5350",
      gradientStartColor: "#ef5350",
      gradientEndColor: "#ff7a00",
      gradientAngle: 90,
      textColor: "#ffffff",
      accentColor: "#ffffff",
      eyebrowColor: "#ffffff",
      titleColor: "#ffffff",
      subtitleColor: "#ffe3dc",
      timerColor: "#ffffff",
      ctaBackgroundColor: "#111111",
      ctaTextColor: "#ffffff",
      layout: "horizontal",
      displayStyle: "blocks",
      borderRadius: 0,
      blockShape: "custom",
      blockBorderRadius: 8,
      timeBoxBackgroundColor: "rgba(255,255,255,0.08)",
      timeBoxTextColor: "#ffffff",
      timeBoxBorderColor: "#ffffff",
      timeBoxBorderStyle: "dashed",
      timeBoxBorderWidth: 2,
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      showTimeLabels: true,
      paddingTop: 18,
      paddingBottom: 18,
    },
  },
];

const TIMER_TYPE_OPTIONS = [
  {
    id: "all",
    name: "All templates",
    description: "Browse every preset.",
    example: "Pick a starting point for any placement.",
  },
  {
    id: "product-card-flags",
    name: "Product Card Flags",
    description: "Useful for catalog grids and collection pages where the timer should accent the card image without covering it.",
    example: "Example: top-right flash flag on a product image.",
  },
  {
    id: "product-detail-banners",
    name: "Product Detail Banners",
    description: "Useful for exact product pages where urgency should sit near the buy action.",
    example: "Example: above-buy-button countdown with CTA.",
  },
  {
    id: "store-strips",
    name: "Store Strips",
    description: "Useful for top, bottom, and sticky announcements across the storefront.",
    example: "Example: sitewide sale bar or checkout nudge strip.",
  },
  {
    id: "inline-content-nudges",
    name: "Inline Content Nudges",
    description: "Useful for compact urgency rows inside content blocks, cards, or landing layouts.",
    example: "Example: inline shipping cutoff or campaign module timer.",
  },
];

function templateCategory(template) {
  if (template?.templateCategory) return template.templateCategory;
  const placements = template?.placementTargets || [];
  if (placements.some((placement) => ["top", "bottom", "sticky-top", "sticky-bottom"].includes(placement))) {
    return "store-strips";
  }
  if (template?.pageTarget === "product" || placements.some((placement) => placement.includes("buy-button"))) {
    return "product-detail-banners";
  }
  return "inline-content-nudges";
}

function isStripTemplate(template) {
  return templateCategory(template) === "store-strips";
}

function templatePreviewPresentation(template) {
  if (template?.previewPresentation) return template.previewPresentation;
  return isStripTemplate(template) ? "strip" : "default";
}

function templateById(templateId) {
  return TIMER_TEMPLATES.find((template) => template.id === templateId) || null;
}

async function getSessionToken(appBridge) {
  try {
    const app = appBridge || window.__shopify_app_bridge__;
    if (!app) return null;
    return await getShopifySessionToken(app);
  } catch {
    return null;
  }
}

function toList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function fromList(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}

function normalizeProductTarget(value) {
  if (typeof value === "string") {
    const legacy = String(value || "").trim();
    if (!legacy) return null;
    return legacy.includes("/Product/")
      ? { id: legacy, handle: "", title: legacy, image: "" }
      : { id: "", handle: legacy, title: legacy, image: "" };
  }
  if (!value || typeof value !== "object") return null;

  const id = String(value.id || "").trim();
  const handle = String(value.handle || "").trim();
  const title = String(value.title || handle || id || "").trim();
  const image = String(value.image || value.featuredImage?.url || "").trim();

  if (!id && !handle) return null;
  return { id, handle, title, image };
}

function normalizeProductTargets(value) {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeProductTarget).filter(Boolean);
}

function normalizeTimer(timer) {
  return {
    ...getDefaultBuilderTimer(),
    ...timer,
    id: timer?.id || timer?._id || null,
    productTargets: normalizeProductTargets(timer?.productTargets),
    collectionTargets: Array.isArray(timer?.collectionTargets) ? timer.collectionTargets : [],
    placementTargets: Array.isArray(timer?.placementTargets)
      ? timer.placementTargets
      : ["top"],
    content: { ...DEFAULT_CONTENT, ...(timer?.content || {}) },
    design: { ...DEFAULT_DESIGN, ...(timer?.design || {}) },
  };
}

function applyTemplateToTimer(timer, template) {
  return {
    ...timer,
    templateId: template.id,
    name: timer.id ? timer.name : template.name,
    pageTarget: template.pageTarget,
    placementTargets: [...template.placementTargets],
    content: { ...timer.content, ...template.content },
    design: { ...timer.design, ...template.design },
  };
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function colorSwatch(color) {
  return <span className="color-swatch" style={{ background: color || "transparent" }} />;
}

function formatShortDate(value) {
  if (!value) return "not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function durationLabel(minutes) {
  const totalMinutes = Number(minutes || 0);
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "0 minutes";
  if (totalMinutes % 1440 === 0) {
    const days = totalMinutes / 1440;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  if (totalMinutes % 60 === 0) {
    const hours = totalMinutes / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `${totalMinutes} minute${totalMinutes === 1 ? "" : "s"}`;
}

function startTriggerRuntimeLabel(startTrigger) {
  return (
    {
      "page-load": "page load",
      navigation: "navigation",
      "custom-event": "custom event",
    }[startTrigger] || "activation"
  );
}

function timerScheduleSummary(timer) {
  if ((timer.startTrigger || "fixed") === "fixed") {
    return `Ends ${formatShortDate(timer.endAt)}`;
  }
  return `Runs for ${durationLabel(timer.durationMinutes)} after ${startTriggerRuntimeLabel(timer.startTrigger)}.`;
}

function pageTargetLabel(value) {
  return (
    {
      product: "Product pages",
      collection: "Collection pages",
      cart: "Cart page",
      all: "All supported pages",
    }[value] || value || "All supported pages"
  );
}

function placementLabel(value) {
  return (
    {
      top: "Top of page",
      bottom: "Bottom of page",
      "app-block": "Theme app section",
      "above-buy-button": "Above buy button",
      "below-buy-button": "Below buy button",
      "cart-footer": "Cart footer",
      "sticky-top": "Sticky top",
      "sticky-bottom": "Sticky bottom",
      manual: "Manual container",
    }[value] || value
  );
}

function isValidHexColor(value) {
  return /^#[0-9a-f]{6}$/i.test(String(value || ""));
}

function themedTemplate(template, themeId) {
  const theme = TEMPLATE_THEMES.find((item) => item.id === themeId) || TEMPLATE_THEMES[0];
  return {
    ...template,
    design: {
      ...template.design,
      ...theme.design,
      backgroundMode: template.design.backgroundMode === "solid" && theme.id === "maroon" ? "solid" : theme.design.backgroundMode,
    },
  };
}

function isValidUrlOrPath(value) {
  if (!String(value || "").trim()) return true;
  if (String(value).startsWith("/")) return true;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function validateBuilderTimer(timer, collectionInput) {
  const errors = {};
  if (!timer.templateId && !timer.id) errors.template = "Choose a template before continuing.";
  if (!String(timer.name || "").trim()) errors.name = "Timer name is required.";
  if (!["fixed", "page-load", "navigation", "custom-event"].includes(timer.startTrigger || "fixed")) {
    errors.startTrigger = "Choose how the timer starts.";
  }
  const durationMinutes = Number(timer.durationMinutes);
  if (!Number.isFinite(durationMinutes) || durationMinutes < 1 || durationMinutes > 10080) {
    errors.durationMinutes = "Use a duration between 1 minute and 7 days.";
  }
  const requiresEndAt = (timer.startTrigger || "fixed") === "fixed";
  if (requiresEndAt && !String(timer.endAt || "").trim()) errors.endAt = "End date and time is required.";
  const startTime = timer.startAt ? new Date(timer.startAt).getTime() : null;
  const endTime = timer.endAt ? new Date(timer.endAt).getTime() : null;
  if (timer.startAt && Number.isNaN(startTime)) errors.startAt = "Start date must be valid.";
  if (requiresEndAt && timer.endAt && Number.isNaN(endTime)) errors.endAt = "End date must be valid.";
  if (requiresEndAt && startTime && endTime && endTime <= startTime) {
    errors.endAt = "End date must be after the start date.";
  }
  if (!Array.isArray(timer.placementTargets) || timer.placementTargets.length === 0) {
    errors.placementTargets = "Choose at least one placement.";
  }
  if (timer.pageTarget === "product" && normalizeProductTargets(timer.productTargets).length > 100) {
    errors.productTargets = "Use 100 or fewer product targets.";
  }
  if (timer.pageTarget === "collection" && toList(collectionInput).length > 100) {
    errors.collectionTargets = "Use 100 or fewer collection targets.";
  }
  if (!String(timer.content?.title || "").trim()) errors.title = "Timer title is required.";
  if (timer.content?.ctaBehavior !== "hidden" && !String(timer.content?.ctaLabel || "").trim()) {
    errors.ctaLabel = "CTA label is required when the CTA is visible.";
  }
  if (!isValidUrlOrPath(timer.content?.ctaUrl)) {
    errors.ctaUrl = "Use a relative path like /collections/sale or a full http/https URL.";
  }
  if (!["vertical", "horizontal"].includes(timer.design?.layout)) {
    errors.layout = "Choose a valid content flow.";
  }
  if (!["blocks", "compact"].includes(timer.design?.displayStyle)) {
    errors.displayStyle = "Choose a valid countdown style.";
  }
  if (!["solid", "gradient"].includes(timer.design?.backgroundMode || "solid")) {
    errors.backgroundMode = "Choose a valid background mode.";
  }
  if (!["square", "circle", "custom"].includes(timer.design?.blockShape || "square")) {
    errors.blockShape = "Choose a valid time box shape.";
  }
  if (!["solid", "dashed", "none"].includes(timer.design?.timeBoxBorderStyle || "none")) {
    errors.timeBoxBorderStyle = "Choose a valid time box border style.";
  }
  const blockBorderRadius = Number(timer.design?.blockBorderRadius);
  if (!Number.isFinite(blockBorderRadius) || blockBorderRadius < 0 || blockBorderRadius > 999) {
    errors.blockBorderRadius = "Use a value between 0 and 999.";
  }
  const timeBoxBorderWidth = Number(timer.design?.timeBoxBorderWidth ?? 0);
  if (!Number.isFinite(timeBoxBorderWidth) || timeBoxBorderWidth < 0 || timeBoxBorderWidth > 12) {
    errors.timeBoxBorderWidth = "Use a value between 0 and 12.";
  }
  const flagSideTailLength = Number(timer.design?.flagSideTailLength ?? 0);
  if (!Number.isFinite(flagSideTailLength) || flagSideTailLength < 0 || flagSideTailLength > 48) {
    errors.flagSideTailLength = "Use a value between 0 and 48.";
  }
  const gradientAngle = Number(timer.design?.gradientAngle ?? 90);
  if (!Number.isFinite(gradientAngle) || gradientAngle < 0 || gradientAngle > 360) {
    errors.gradientAngle = "Use a value between 0 and 360.";
  }
  if (
    timer.design?.showDays === false &&
    timer.design?.showHours === false &&
    timer.design?.showMinutes === false &&
    timer.design?.showSeconds === false
  ) {
    errors.timeUnits = "Keep at least one countdown box visible.";
  }
  [
    "backgroundColor",
    "gradientStartColor",
    "gradientEndColor",
    "textColor",
    "accentColor",
    "eyebrowColor",
    "titleColor",
    "subtitleColor",
    "timerColor",
    "ctaBackgroundColor",
    "ctaTextColor",
    "brandingBackgroundColor",
    "brandingTextColor",
    "timeBoxTextColor",
    "timeBoxBorderColor",
  ].forEach((key) => {
    if (!isValidHexColor(timer.design?.[key])) {
      errors[key] = "Use a valid hex color, for example #ff404c.";
    }
  });
  [
    "fontSize",
    "eyebrowFontSize",
    "titleFontSize",
    "subtitleFontSize",
    "timerFontSize",
    "ctaFontSize",
  ].forEach((key) => {
    const value = Number(timer.design?.[key]);
    if (!Number.isFinite(value) || value < 8 || value > 120) {
      errors[key] = "Use a value between 8 and 120.";
    }
  });
  return errors;
}

function stepHasErrors(step, errors) {
  const fieldsByStep = {
    template: ["template"],
    settings: [
      "name",
      "startTrigger",
      "durationMinutes",
      "startAt",
      "endAt",
      "placementTargets",
      "productTargets",
      "collectionTargets",
    ],
    content: ["title", "ctaLabel", "ctaUrl"],
    design: [
      "layout",
      "displayStyle",
      "backgroundMode",
      "blockShape",
      "blockBorderRadius",
      "timeBoxBorderStyle",
      "timeBoxBorderWidth",
      "flagSideTailLength",
      "timeUnits",
      "backgroundColor",
      "gradientStartColor",
      "gradientEndColor",
      "gradientAngle",
      "textColor",
      "accentColor",
      "eyebrowColor",
      "titleColor",
      "subtitleColor",
      "timerColor",
      "ctaBackgroundColor",
      "ctaTextColor",
      "brandingBackgroundColor",
      "brandingTextColor",
      "timeBoxTextColor",
      "timeBoxBorderColor",
      "fontSize",
      "eyebrowFontSize",
      "titleFontSize",
      "subtitleFontSize",
      "timerFontSize",
      "ctaFontSize",
    ],
  };
  return (fieldsByStep[step] || []).some((field) => errors[field]);
}

export default function Settings({ shop, testMode = false, appBridge = null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingPlanLoading, setBillingPlanLoading] = useState(null);
  const [activePlan, setActivePlan] = useState("free");
  const [toast, setToast] = useState(null);
  const [activeView, setActiveView] = useState("dashboard");
  const [builderStep, setBuilderStep] = useState("template");
  const [selectedTemplateTheme, setSelectedTemplateTheme] = useState("coral");

  const [timers, setTimers] = useState([]);
  const [deepLinks, setDeepLinks] = useState({});
  const [themeStatus, setThemeStatus] = useState({ appEmbedEnabled: false, appBlockPresent: false });
  const [analytics, setAnalytics] = useState({
    totals: { impressions: 0, clicks: 0, expired: 0, ctr: 0 },
    timers: [],
  });
  const [builderTimer, setBuilderTimer] = useState(getDefaultBuilderTimer);
  const [collectionTargetInput, setCollectionTargetInput] = useState("");
  const [builderErrors, setBuilderErrors] = useState({});
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productPageInfo, setProductPageInfo] = useState({ hasNextPage: false, endCursor: null });
  const [productLoading, setProductLoading] = useState(false);
  const [productLoadingMore, setProductLoadingMore] = useState(false);

  const [hideBranding, setHideBranding] = useState(false);
  const themeStatusRefreshRef = useRef({ inFlight: false, lastStartedAt: 0 });

  const isPro = activePlan === "pro_monthly" || activePlan === "pro_yearly";

  const tokenFetch = useCallback(
    async (url, options = {}) => {
      const token = await getSessionToken(appBridge);
      return fetch(url, {
        ...options,
        headers: {
          ...(options.body ? { "Content-Type": "application/json" } : {}),
          ...(options.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
    [appBridge]
  );

  const refreshThemeStatus = useCallback(async () => {
    try {
      const res = await tokenFetch(`${API_BASE}/api/theme/status`);
      if (!res.ok) return;
      setThemeStatus(await res.json());
    } catch (e) {
      // Keep the last known setup state if Shopify status cannot be refreshed.
    }
  }, [tokenFetch]);

  const loadAdminData = useCallback(async () => {
    const [timersRes, linksRes, analyticsRes, statusRes] = await Promise.allSettled([
      tokenFetch(`${API_BASE}/api/timers`),
      tokenFetch(`${API_BASE}/api/theme/deep-links`),
      tokenFetch(`${API_BASE}/api/analytics/summary`),
      tokenFetch(`${API_BASE}/api/theme/status`),
    ]);

    if (timersRes.status === "fulfilled" && timersRes.value.ok) {
      const data = await timersRes.value.json();
      setTimers((data.timers || []).map(normalizeTimer));
    }

    if (linksRes.status === "fulfilled" && linksRes.value.ok) {
      setDeepLinks(await linksRes.value.json());
    }

    if (statusRes.status === "fulfilled" && statusRes.value.ok) {
      try {
        setThemeStatus(await statusRes.value.json());
      } catch (e) {
        // ignore
      }
    }

    if (analyticsRes.status === "fulfilled" && analyticsRes.value.ok) {
      const data = await analyticsRes.value.json();
      setAnalytics({
        totals: data.totals || { impressions: 0, clicks: 0, expired: 0, ctr: 0 },
        timers: data.timers || [],
      });
    }
  }, [tokenFetch]);

  useEffect(() => {
    if (testMode) {
      setLoading(false);
      return;
    }

    if (!shop) return;

    async function loadConfig() {
      try {
        const configRes = await fetch(
          `${API_BASE}/api/config/get?shop=${encodeURIComponent(shop)}`
        );
        if (configRes.ok) {
          const cfg = await configRes.json();
          setHideBranding(cfg.hideBranding ?? false);
        }

        const planRes = await tokenFetch(`${API_BASE}/api/plan`);
        if (planRes.ok) {
          const planData = await planRes.json();
          setActivePlan(planData.activePlan ?? "free");
        }

        await loadAdminData();
      } catch (err) {
        console.error("Failed to load admin data:", err);
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, [shop, testMode, tokenFetch, loadAdminData]);

  useEffect(() => {
    if (testMode || !shop || loading) return undefined;

    let stopped = false;
    const refresh = async () => {
      const now = Date.now();
      if (
        stopped ||
        themeStatusRefreshRef.current.inFlight ||
        now - themeStatusRefreshRef.current.lastStartedAt < 1000
      ) {
        return;
      }

      themeStatusRefreshRef.current.inFlight = true;
      themeStatusRefreshRef.current.lastStartedAt = now;
      try {
        await refreshThemeStatus();
      } finally {
        themeStatusRefreshRef.current.inFlight = false;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopped = true;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loading, refreshThemeStatus, shop, testMode]);

  const updateBuilder = useCallback((path, value) => {
    setBuilderErrors((current) => {
      const next = { ...current };
      const key = path.includes(".") ? path.split(".")[1] : path;
      delete next[key];
      if (["showDays", "showHours", "showMinutes", "showSeconds"].includes(key)) {
        delete next.timeUnits;
      }
      if (path === "template") delete next.template;
      return next;
    });
    setBuilderTimer((current) => {
      if (path === "template") {
        return applyTemplateToTimer(current, value);
      }
      const next = { ...current };
      if (path.includes(".")) {
        const [root, key] = path.split(".");
        next[root] = { ...next[root], [key]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  }, []);

  const editTimer = useCallback((timer) => {
    const normalized = normalizeTimer(timer);
    setBuilderTimer(normalized);
    setCollectionTargetInput(fromList(normalized.collectionTargets));
    setProductSearchQuery("");
    setProductOptions([]);
    setProductPageInfo({ hasNextPage: false, endCursor: null });
    setBuilderErrors({});
    setBuilderStep("settings");
    setActiveView("builder");
  }, []);

  const createTimer = useCallback(() => {
    setBuilderTimer(getDefaultBuilderTimer());
    setCollectionTargetInput("");
    setProductSearchQuery("");
    setProductOptions([]);
    setProductPageInfo({ hasNextPage: false, endCursor: null });
    setBuilderErrors({});
    setBuilderStep("template");
    setActiveView("builder");
  }, []);

  const loadProductOptions = useCallback(
    async ({ append = false, cursor = "", query = "" } = {}) => {
      const trimmedQuery = String(query || "").trim();
      const params = new URLSearchParams({ limit: "20" });
      if (cursor) params.set("cursor", cursor);
      if (trimmedQuery) params.set("query", trimmedQuery);

      if (append) setProductLoadingMore(true);
      else setProductLoading(true);

      try {
        const res = await tokenFetch(`${API_BASE}/api/timers/products?${params.toString()}`);
        if (!res.ok) throw new Error("Failed to load products");
        const data = await res.json();
        const nextProducts = normalizeProductTargets(data.products);
        setProductOptions((current) => {
          if (!append) return nextProducts;
          const seen = new Set(current.map((item) => item.id || item.handle));
          return current.concat(nextProducts.filter((item) => !seen.has(item.id || item.handle)));
        });
        setProductPageInfo(data.pageInfo || { hasNextPage: false, endCursor: null });
      } catch (err) {
        setToast({ content: err.message, error: true });
      } finally {
        setProductLoading(false);
        setProductLoadingMore(false);
      }
    },
    [tokenFetch]
  );

  useEffect(() => {
    if (activeView !== "builder" || builderStep !== "settings" || builderTimer.pageTarget !== "product") return undefined;
    const timeoutId = window.setTimeout(() => {
      loadProductOptions({ append: false, query: productSearchQuery });
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [activeView, builderStep, builderTimer.pageTarget, loadProductOptions, productSearchQuery]);

  const saveTimer = useCallback(async () => {
    const payload = {
      ...builderTimer,
      productTargets: normalizeProductTargets(builderTimer.productTargets),
      collectionTargets: toList(collectionTargetInput),
      status: builderTimer.timerEnabled ? "active" : "draft",
    };

    const validationErrors = validateBuilderTimer(payload, collectionTargetInput);
    if (Object.keys(validationErrors).length > 0) {
      setBuilderErrors(validationErrors);
      setToast({ content: Object.values(validationErrors)[0], error: true });
      return;
    }

    setSaving(true);
    try {
      const url = payload.id
        ? `${API_BASE}/api/timers/${encodeURIComponent(payload.id)}`
        : `${API_BASE}/api/timers`;
      const res = await tokenFetch(url, {
        method: payload.id ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Timer save failed");
      }
      const data = await res.json();
      const saved = normalizeTimer(data.timer || payload);
      setTimers((current) => {
        const withoutSaved = current.filter((timer) => timer.id !== saved.id);
        return [saved, ...withoutSaved];
      });
      setBuilderTimer(saved);
      setToast({ content: "Timer saved.", error: false });
      await loadAdminData();
      setActiveView("timers");
    } catch (err) {
      setToast({ content: err.message, error: true });
    } finally {
      setSaving(false);
    }
  }, [builderTimer, collectionTargetInput, loadAdminData, tokenFetch]);

  const deleteTimer = useCallback(
    async (timerId) => {
      try {
        const res = await tokenFetch(`${API_BASE}/api/timers/${encodeURIComponent(timerId)}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error("Delete failed");
        setTimers((current) => current.filter((timer) => timer.id !== timerId));
        setToast({ content: "Timer deleted.", error: false });
      } catch (err) {
        setToast({ content: err.message, error: true });
      }
    },
    [tokenFetch]
  );

  const startBillingCheckout = useCallback(
    async (plan) => {
      setBillingPlanLoading(plan);
      try {
        const res = await tokenFetch(`${API_BASE}/api/billing/subscribe`, {
          method: "POST",
          body: JSON.stringify({ plan }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || data.errors?.[0]?.message || "Unable to start checkout");
        }
        if (!data.confirmationUrl) {
          throw new Error("Shopify did not return a billing confirmation link");
        }
        window.open(data.confirmationUrl, "_top");
      } catch (err) {
        setToast({ content: err.message, error: true });
      } finally {
        setBillingPlanLoading(null);
      }
    },
    [tokenFetch]
  );

  const openDeepLink = useCallback((url) => {
    if (!url) {
      setToast({ content: "Theme link is not available yet.", error: true });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const checklist = useMemo(
    () => [
      {
        label: "Enable app embed",
        done: Boolean(themeStatus.appEmbedEnabled),
        countsTowardProgress: false,
      },
      { label: "Add a timer section", done: Boolean(themeStatus.appBlockPresent) },
      { label: "Confirm timer is active", done: timers.some((timer) => timer.timerEnabled) },
    ],
    [themeStatus, timers]
  );

  if (loading) {
    return (
      <Page title="Quick Urgency Timer">
        <InlineStack align="center">
          <Spinner accessibilityLabel="Loading settings" size="large" />
        </InlineStack>
      </Page>
    );
  }

  const totals = analytics.totals || {};
  const primaryAction =
    activeView === "builder"
      ? undefined
      : { content: "Create timer", onAction: createTimer };

  return (
    <Frame>
      {toast ? (
        <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
      ) : null}
      <Page
        title="Quick Urgency Timer"
        subtitle="Manage storefront countdowns, placements, and activation."
        titleMetadata={<Badge tone={isPro ? "success" : "info"}>{isPro ? "Pro" : "Free"}</Badge>}
        primaryAction={primaryAction}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack gap="200" blockAlign="center">
                {NAV_ITEMS.map((item) => (
                  <Button
                    key={item.id}
                    pressed={activeView === item.id}
                    onClick={() => setActiveView(item.id)}
                  >
                    {item.label}
                  </Button>
                ))}
              </InlineStack>
            </Card>
          </Layout.Section>

          {activeView === "dashboard" ? (
            <Dashboard
              activePlan={activePlan}
              billingPlanLoading={billingPlanLoading}
              checklist={checklist}
              deepLinks={deepLinks}
              onStartBillingCheckout={startBillingCheckout}
              openDeepLink={openDeepLink}
              totals={totals}
              timers={timers}
              onCreateTimer={createTimer}
            />
          ) : null}

          {activeView === "timers" ? (
            <TimersList
              timers={timers}
              onCreateTimer={createTimer}
              onEditTimer={editTimer}
              onDeleteTimer={deleteTimer}
            />
          ) : null}

          {activeView === "builder" ? (
            <Builder
              builderStep={builderStep}
              setBuilderStep={setBuilderStep}
              timer={builderTimer}
              updateBuilder={updateBuilder}
              collectionTargetInput={collectionTargetInput}
              setCollectionTargetInput={setCollectionTargetInput}
              productSearchQuery={productSearchQuery}
              setProductSearchQuery={setProductSearchQuery}
              productOptions={productOptions}
              productPageInfo={productPageInfo}
              productLoading={productLoading}
              productLoadingMore={productLoadingMore}
              loadProductOptions={loadProductOptions}
              builderErrors={builderErrors}
              setBuilderErrors={setBuilderErrors}
              selectedTemplateTheme={selectedTemplateTheme}
              setSelectedTemplateTheme={setSelectedTemplateTheme}
              saveTimer={saveTimer}
              saving={saving}
              hideBranding={isPro && hideBranding}
            />
          ) : null}
        </Layout>
      </Page>
    </Frame>
  );
}

function Dashboard({
  activePlan,
  billingPlanLoading,
  checklist,
  deepLinks,
  onStartBillingCheckout,
  openDeepLink,
  totals,
  timers,
  onCreateTimer,
}) {
  const activeTimers = timers.filter((timer) => timer.timerEnabled).length;
  const progressItems = checklist.filter((item) => item.countsTowardProgress !== false);
  const completedProgressItems = progressItems.filter((item) => item.done).length;
  const isSetupComplete =
    progressItems.length > 0 && completedProgressItems === progressItems.length;

  return (
    <>
      <Layout.Section>
        <InlineGrid columns={{ xs: 1, md: 5 }} gap="400">
          <MetricCard label="Total timers" value={formatNumber(timers.length)} />
          <MetricCard label="Active timers" value={formatNumber(activeTimers)} />
          <MetricCard label="Impressions" value={formatNumber(totals.impressions)} />
          <MetricCard label="Clicks" value={formatNumber(totals.clicks)} />
          <MetricCard label="CTR" value={`${Number(totals.ctr || 0).toFixed(2)}%`} />
        </InlineGrid>
      </Layout.Section>

      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h2">
                Setup checklist
              </Text>
              <Badge tone={isSetupComplete ? "success" : "attention"}>
                {completedProgressItems} of {progressItems.length}
              </Badge>
            </InlineStack>
            <BlockStack gap="200">
              {checklist.map((item) => (
                <SetupAction
                  key={item.label}
                  item={item}
                  deepLinks={deepLinks}
                  openDeepLink={openDeepLink}
                  onCreateTimer={onCreateTimer}
                />
              ))}
            </BlockStack>
          </BlockStack>
        </Card>
      </Layout.Section>

      {activePlan === "free" ? (
        <Layout.Section>
          <UpgradeCard
            billingPlanLoading={billingPlanLoading}
            onStartBillingCheckout={onStartBillingCheckout}
          />
        </Layout.Section>
      ) : null}
    </>
  );
}

function UpgradeCard({ billingPlanLoading, onStartBillingCheckout }) {
  const yearlySelected = billingPlanLoading === "pro_yearly";
  const monthlySelected = billingPlanLoading === "pro_monthly";

  return (
    <Card>
      <div className="upgrade-card">
        <BlockStack gap="300">
          <Badge tone="attention">Pro</Badge>
          <Text variant="headingLg" as="h2">
            Upgrade to Pro
          </Text>
          <Text tone="subdued" as="p">
            Unlock branded storefront polish, premium styling control, and faster launch-ready timer experiences.
          </Text>
          <div className="upgrade-card__features" aria-label="Pro plan benefits">
            <span>Remove Quick Urgency Timer branding</span>
            <span>Keep advanced design controls live on every timer</span>
            <span>Pick monthly or annual billing inside Shopify</span>
          </div>
        </BlockStack>
        <div className="upgrade-card__actions">
          <Button
            variant="primary"
            loading={yearlySelected}
            disabled={billingPlanLoading !== null}
            onClick={() => onStartBillingCheckout("pro_yearly")}
          >
            Start yearly plan
          </Button>
          <Button
            disabled={billingPlanLoading !== null}
            loading={monthlySelected}
            onClick={() => onStartBillingCheckout("pro_monthly")}
          >
            Start monthly plan
          </Button>
        </div>
      </div>
    </Card>
  );
}

function SetupAction({ item, deepLinks, openDeepLink, onCreateTimer }) {
  const actionByLabel = {
    "Enable app embed": () => openDeepLink(deepLinks.appEmbedUrl),
    "Add a timer section": () => openDeepLink(deepLinks.appBlockUrl),
    "Confirm timer is active": onCreateTimer,
  };
  const action = actionByLabel[item.label];
  return (
    <button
      type="button"
      className={`setup-action ${item.done ? "setup-action--done" : ""}`}
      onClick={action}
      aria-label={item.label}
    >
      <span className="setup-action__status" aria-hidden="true">
        {item.done ? "✓" : "→"}
      </span>
      <span className="setup-action__copy">
        <strong>{item.label}</strong>
        <span>{item.done ? "Connected" : "Open setup action"}</span>
      </span>
      <span className="setup-action__badge">{item.done ? "Connected" : "Action"}</span>
    </button>
  );
}

function MetricCard({ label, value }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text tone="subdued" as="p">
          {label}
        </Text>
        <Text variant="headingLg" as="p">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

function TimerRows({ timers, onEditTimer, onDeleteTimer = null, compact = false }) {
  return (
    <BlockStack gap="200">
      {timers.map((timer) => {
        const normalized = normalizeTimer(timer);
        const placements = (normalized.placementTargets || []).map(placementLabel).join(", ");
        return (
          <div className="timer-row" key={normalized.id || normalized.name}>
            <BlockStack gap="100">
              <InlineStack gap="200" blockAlign="center">
                <Text variant="headingSm" as="h3">
                  {normalized.name}
                </Text>
                <Badge tone={normalized.timerEnabled ? "success" : "attention"}>
                  {normalized.timerEnabled ? "Active" : normalized.status || "Draft"}
                </Badge>
              </InlineStack>
              <Text tone="subdued" as="p">
                {pageTargetLabel(normalized.pageTarget)} · {placements || "No placement"} ·{" "}
                {timerScheduleSummary(normalized)}
              </Text>
              <Text tone="subdued" as="p">
                {normalized.content.title || "Untitled timer"} ·{" "}
                {formatNumber(normalized.impressions)} impressions ·{" "}
                {formatNumber(normalized.clicks)} clicks
              </Text>
            </BlockStack>
            {!compact ? (
              <InlineStack gap="200">
                <Button onClick={() => onEditTimer(normalized)}>Edit</Button>
                {onDeleteTimer && normalized.id ? (
                  <Button tone="critical" onClick={() => onDeleteTimer(normalized.id)}>
                    Delete
                  </Button>
                ) : null}
              </InlineStack>
            ) : null}
          </div>
        );
      })}
    </BlockStack>
  );
}

function EmptyTimerState({ onCreateTimer }) {
  return (
    <BlockStack gap="300">
      <Text variant="headingSm" as="h3">
        No timers yet
      </Text>
      <Text tone="subdued" as="p">
        Create your first timer to start collecting analytics.
      </Text>
      <Button onClick={onCreateTimer}>Create timer</Button>
    </BlockStack>
  );
}

function TimersList({ timers, onCreateTimer, onEditTimer, onDeleteTimer }) {
  return (
    <Layout.Section>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Timers
            </Text>
            <Button variant="primary" onClick={onCreateTimer}>
              New timer
            </Button>
          </InlineStack>
          {timers.length ? (
            <TimerRows timers={timers} onEditTimer={onEditTimer} onDeleteTimer={onDeleteTimer} />
          ) : (
            <EmptyTimerState onCreateTimer={onCreateTimer} />
          )}
        </BlockStack>
      </Card>
    </Layout.Section>
  );
}

function Builder({
  builderStep,
  setBuilderStep,
  timer,
  updateBuilder,
  collectionTargetInput,
  setCollectionTargetInput,
  productSearchQuery,
  setProductSearchQuery,
  productOptions,
  productPageInfo,
  productLoading,
  productLoadingMore,
  loadProductOptions,
  builderErrors,
  setBuilderErrors,
  selectedTemplateTheme,
  setSelectedTemplateTheme,
  saveTimer,
  saving,
  hideBranding,
}) {
  const previewTimer = {
    ...timer,
    productTargets: normalizeProductTargets(timer.productTargets),
    collectionTargets: toList(collectionTargetInput),
  };
  const currentStepIndex = BUILDER_STEPS.findIndex((step) => step.id === builderStep);
  const safeStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;
  const canGoBack = safeStepIndex > 0;
  const canGoNext = safeStepIndex < BUILDER_STEPS.length - 1;
  const validateAndStore = () => {
    const errors = validateBuilderTimer(previewTimer, collectionTargetInput);
    setBuilderErrors(errors);
    return errors;
  };
  const goToStep = (index) => {
    const errors = validateAndStore();
    const targetIndex = Math.max(0, Math.min(index, BUILDER_STEPS.length - 1));
    if (targetIndex > safeStepIndex && stepHasErrors(builderStep, errors)) return;
    setBuilderStep(BUILDER_STEPS[targetIndex].id);
  };
  const selectedTemplate = TIMER_TEMPLATES.find((template) => template.id === timer.templateId);
  const themedSelectedTemplate = selectedTemplate ? themedTemplate(selectedTemplate, selectedTemplateTheme) : null;
  const fixedSchedule = (timer.startTrigger || "fixed") === "fixed";

  const selectTemplate = (template) => {
    setBuilderTimerFromTemplate(updateBuilder, themedTemplate(template, selectedTemplateTheme));
  };

  const changeTemplateTheme = (themeId) => {
    setSelectedTemplateTheme(themeId);
    if (selectedTemplate) {
      setBuilderTimerFromTemplate(updateBuilder, themedTemplate(selectedTemplate, themeId));
    }
  };

  const handleStartTriggerChange = (value) => {
    updateBuilder("startTrigger", value);
    if (value === "fixed") {
      if (!timer.endAt) updateBuilder("endAt", getDefaultBuilderTimer().endAt);
      return;
    }
    updateBuilder("endAt", "");
  };

  return (
    <>
      <Layout.Section>
        <Card>
          <div className="builder-shell">
            <div className="builder-stepper" aria-label="Timer builder steps">
              {BUILDER_STEPS.map((step, index) => (
                <button
                  type="button"
                  key={step.id}
                  className={`builder-stepper__item ${
                    index === safeStepIndex ? "builder-stepper__item--active" : ""
                  } ${index < safeStepIndex ? "builder-stepper__item--complete" : ""}`}
                  onClick={() => goToStep(index)}
                >
                  <span>{index + 1}</span>
                  {step.label}
                </button>
              ))}
            </div>
            <Divider />
            {builderStep === "template" ? (
              <div className="builder-step-scroll">
                <TemplateStep
                  selectedTemplateId={selectedTemplate?.id || timer.templateId}
                  error={builderErrors.template}
                  onSelectTemplate={selectTemplate}
                  selectedThemeId={selectedTemplateTheme}
                  onSelectTheme={changeTemplateTheme}
                  timerEndTime={timer.endAt}
                  hideBranding={hideBranding}
                />
              </div>
            ) : null}
            {builderStep === "settings" ? (
              <div className="builder-step-scroll">
                <SettingsStep
                  timer={timer}
                  updateBuilder={updateBuilder}
                  fixedSchedule={fixedSchedule}
                  onStartTriggerChange={handleStartTriggerChange}
                  collectionTargetInput={collectionTargetInput}
                  setCollectionTargetInput={setCollectionTargetInput}
                  productSearchQuery={productSearchQuery}
                  setProductSearchQuery={setProductSearchQuery}
                  productOptions={productOptions}
                  productPageInfo={productPageInfo}
                  productLoading={productLoading}
                  productLoadingMore={productLoadingMore}
                  loadProductOptions={loadProductOptions}
                  errors={builderErrors}
                />
              </div>
            ) : null}
            {builderStep === "content" ? (
              <div className="builder-step-scroll">
                <ContentStep timer={timer} updateBuilder={updateBuilder} errors={builderErrors} />
              </div>
            ) : null}
            {builderStep === "design" ? (
              <div className="builder-step-scroll">
                <DesignStep timer={timer} updateBuilder={updateBuilder} errors={builderErrors} />
              </div>
            ) : null}
            <div className="builder-actions">
              <InlineStack gap="200" align="space-between">
                <Button onClick={() => goToStep(safeStepIndex - 1)} disabled={!canGoBack}>
                  Back
                </Button>
                <InlineStack gap="200">
                  {canGoNext ? (
                    <Button
                      variant="primary"
                      onClick={() => goToStep(safeStepIndex + 1)}
                      disabled={builderStep === "template" && !timer.templateId}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button onClick={saveTimer} loading={saving} variant="primary">
                      Save timer
                    </Button>
                  )}
                </InlineStack>
              </InlineStack>
            </div>
          </div>
        </Card>
      </Layout.Section>
      <Layout.Section variant="oneThird">
        <PreviewPanel timer={previewTimer} hideBranding={hideBranding} selectedTemplate={themedSelectedTemplate} />
      </Layout.Section>
    </>
  );
}

function setBuilderTimerFromTemplate(updateBuilder, template) {
  updateBuilder("template", template);
}

function TemplateStep({
  selectedTemplateId,
  error,
  onSelectTemplate,
  selectedThemeId,
  onSelectTheme,
  timerEndTime,
  hideBranding,
}) {
  const [selectedTimerType, setSelectedTimerType] = useState("all");
  const visibleTemplates =
    selectedTimerType === "all"
      ? TIMER_TEMPLATES
      : TIMER_TEMPLATES.filter((template) => templateCategory(template) === selectedTimerType);

  return (
    <BlockStack gap="500">
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="400">
          <BlockStack gap="100">
            <Text variant="headingLg" as="h2">
              Select countdown timer type
            </Text>
            <Text tone="subdued" as="p">
              Choose the timer surface first, then pick a polished preset and tune the details.
            </Text>
          </BlockStack>
          <InlineStack gap="200" blockAlign="center">
            <Text variant="headingSm" as="p">
              Color theme
            </Text>
            <div className="template-theme-selector" role="radiogroup" aria-label="Color theme">
              {TEMPLATE_THEMES.map((theme) => (
                <button
                  type="button"
                  key={theme.id}
                  className={`template-theme-selector__swatch ${
                    selectedThemeId === theme.id ? "template-theme-selector__swatch--selected" : ""
                  }`}
                  style={{ background: theme.swatch }}
                  aria-label={`${theme.name} theme`}
                  aria-checked={selectedThemeId === theme.id}
                  role="radio"
                  onClick={() => onSelectTheme(theme.id)}
                />
              ))}
            </div>
          </InlineStack>
        </InlineStack>
        {error ? (
          <Text tone="critical" as="p">
            {error}
          </Text>
        ) : null}
      </BlockStack>
      <div className="timer-type-grid" role="list" aria-label="Countdown timer types">
        {TIMER_TYPE_OPTIONS.map((type) => (
          <button
            type="button"
            key={type.id}
            className={`timer-type-card ${selectedTimerType === type.id ? "timer-type-card--selected" : ""}`}
            onClick={() => setSelectedTimerType(type.id)}
            aria-pressed={selectedTimerType === type.id}
          >
            <span className={`timer-type-card__diagram timer-type-card__diagram--${type.id}`} aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <strong>{type.name}</strong>
            <span>{type.description}</span>
            <em>{type.example}</em>
          </button>
        ))}
      </div>
      <div className="template-workspace">
        <div className="template-grid">
          {visibleTemplates.map((template) => {
            const themed = themedTemplate(template, selectedThemeId);
            const stripTemplate = isStripTemplate(template);
            const categoryMeta = TIMER_TYPE_OPTIONS.find((type) => type.id === templateCategory(template));
            return (
            <button
              type="button"
              key={template.id}
              className={`template-card template-card--${template.tone} ${
                stripTemplate ? "template-card--strip" : ""
              } ${
                selectedTemplateId === template.id ? "template-card--selected" : ""
              }`}
              onClick={() => onSelectTemplate(template)}
              aria-label={`Select ${template.name}`}
            >
              <span className="template-card__preview" aria-hidden="true">
                <TimerPreview
                  timerEnabled
                  timerEndTime={timerEndTime}
                  content={themed.content}
                  design={themed.design}
                  hideBranding={hideBranding}
                  demoDurationSeconds={5400}
                  presentation={templatePreviewPresentation(template)}
                  previewMode
                />
              </span>
              <span className="template-card__body">
                {categoryMeta ? <span className="template-card__meta">{categoryMeta.name}</span> : null}
                <strong>{template.name}</strong>
                <span>{template.description}</span>
              </span>
              <span className="template-card__action">
                {selectedTemplateId === template.id ? "Selected" : "Select"}
              </span>
            </button>
          );
          })}
        </div>
      </div>
    </BlockStack>
  );
}

function SettingsStep({
  timer,
  updateBuilder,
  fixedSchedule,
  onStartTriggerChange,
  collectionTargetInput,
  setCollectionTargetInput,
  productSearchQuery,
  setProductSearchQuery,
  productOptions,
  productPageInfo,
  productLoading,
  productLoadingMore,
  loadProductOptions,
  errors = {},
}) {
  const togglePlacement = (value) => {
    const current = new Set(timer.placementTargets || []);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    updateBuilder("placementTargets", Array.from(current));
  };
  const selectedProductTargets = normalizeProductTargets(timer.productTargets);
  const selectedKeys = new Set(selectedProductTargets.map((item) => item.id || item.handle));
  const addProductTarget = (product) => {
    if (selectedKeys.has(product.id || product.handle)) return;
    updateBuilder("productTargets", [...selectedProductTargets, product]);
  };
  const removeProductTarget = (product) => {
    updateBuilder(
      "productTargets",
      selectedProductTargets.filter((item) => (item.id || item.handle) !== (product.id || product.handle))
    );
  };

  return (
    <BlockStack gap="400">
      <div className="builder-section">
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Timer setup
          </Text>
          <FormLayout>
            <TextField
              label="Timer name"
              value={timer.name}
              onChange={(value) => updateBuilder("name", value)}
              error={errors.name}
              autoComplete="off"
            />
            <Checkbox
              label="Enable timer"
              checked={timer.timerEnabled}
              onChange={(value) => updateBuilder("timerEnabled", value)}
            />
          </FormLayout>
        </BlockStack>
      </div>
      <div className="builder-section">
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Start behavior
          </Text>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label="Timer starts"
                helpText="Choose whether the countdown uses fixed dates or starts from a storefront event."
                options={[
                  { label: "Use scheduled start and end dates", value: "fixed" },
                  { label: "When shopper lands on the page", value: "page-load" },
                  { label: "When shopper navigates to a matching page", value: "navigation" },
                  { label: "When a custom storefront event fires", value: "custom-event" },
                ]}
                value={timer.startTrigger || "fixed"}
                onChange={onStartTriggerChange}
                error={errors.startTrigger}
              />
              <TextField
                label="Timer duration in minutes"
                type="number"
                value={String(timer.durationMinutes ?? 90)}
                onChange={(value) => updateBuilder("durationMinutes", Number(value))}
                helpText="Used for page-load, navigation, and custom-event timers."
                error={errors.durationMinutes}
                autoComplete="off"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                label="Start date and time"
                type="datetime-local"
                value={timer.startAt || ""}
                onChange={(value) => updateBuilder("startAt", value)}
                helpText={fixedSchedule ? "Optional for scheduled timers." : "Optional date when this evergreen timer becomes eligible to run."}
                error={errors.startAt}
                autoComplete="off"
              />
              {fixedSchedule ? (
                <TextField
                  label="End date and time"
                  type="datetime-local"
                  value={timer.endAt || ""}
                  onChange={(value) => updateBuilder("endAt", value)}
                  helpText="Required for scheduled timers."
                  error={errors.endAt}
                  autoComplete="off"
                />
              ) : (
                <BlockStack gap="100">
                  <Text as="p" tone="subdued">
                    {`This timer runs for ${durationLabel(timer.durationMinutes)} after ${startTriggerRuntimeLabel(
                      timer.startTrigger
                    )}.`}
                  </Text>
                  <Text as="p" tone="subdued">
                    No fixed end date is required for this trigger.
                  </Text>
                </BlockStack>
              )}
            </FormLayout.Group>
          </FormLayout>
        </BlockStack>
      </div>
      <div className="builder-section">
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Targeting and placement
          </Text>
          <FormLayout>
            <Select
              label="Page targeting"
              options={[
                { label: "Product pages", value: "product" },
                { label: "Collection pages", value: "collection" },
                { label: "Cart page", value: "cart" },
                { label: "All supported pages", value: "all" },
              ]}
              value={timer.pageTarget}
              onChange={(value) => updateBuilder("pageTarget", value)}
            />
            <InlineStack gap="400">
              {[
                ["top", "Top of page"],
                ["bottom", "Bottom of page"],
                ["app-block", "Theme app section"],
                ["above-buy-button", "Above buy button"],
                ["below-buy-button", "Below buy button"],
                ["cart-footer", "Cart footer"],
                ["sticky-top", "Sticky top"],
                ["sticky-bottom", "Sticky bottom"],
                ["manual", "Manual container"],
              ].map(([target, label]) => (
                <Checkbox
                  key={target}
                  label={label}
                  checked={(timer.placementTargets || []).includes(target)}
                  onChange={() => togglePlacement(target)}
                />
              ))}
            </InlineStack>
            {errors.placementTargets ? (
              <Text tone="critical" as="p">
                {errors.placementTargets}
              </Text>
            ) : null}
            {timer.pageTarget === "product" ? (
              <div className="product-picker">
                <TextField
                  label="Products"
                  value={productSearchQuery}
                  onChange={setProductSearchQuery}
                  helpText="Select the products that should show this timer on product pages and listing cards. Leave empty for all products."
                  error={errors.productTargets}
                  autoComplete="off"
                  placeholder="Search products"
                />
                {selectedProductTargets.length > 0 ? (
                  <BlockStack gap="200">
                    <Text variant="bodySm" as="p">
                      Selected products
                    </Text>
                    <div className="product-picker__selected">
                      {selectedProductTargets.map((product) => (
                        <button
                          type="button"
                          key={product.id || product.handle}
                          className="product-picker__selected-item"
                          onClick={() => removeProductTarget(product)}
                          aria-label={`Remove ${product.title || product.handle}`}
                        >
                          <span>{product.title || product.handle}</span>
                          <span aria-hidden="true">x</span>
                        </button>
                      ))}
                    </div>
                  </BlockStack>
                ) : null}
                <div className="product-picker__results" aria-live="polite">
                  {productLoading ? (
                    <InlineStack align="center">
                      <Spinner accessibilityLabel="Loading products" size="small" />
                    </InlineStack>
                  ) : null}
                  {!productLoading && productOptions.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No products found for this search.
                    </Text>
                  ) : null}
                  {!productLoading
                    ? productOptions.map((product) => {
                        const productKey = product.id || product.handle;
                        const selected = selectedKeys.has(productKey);
                        return (
                          <button
                            type="button"
                            key={productKey}
                            className={`product-picker__option ${
                              selected ? "product-picker__option--selected" : ""
                            }`}
                            onClick={() => addProductTarget(product)}
                            disabled={selected}
                            aria-label={`${selected ? "Selected" : "Select"} ${product.title || product.handle}`}
                          >
                            <span className="product-picker__option-copy">
                              <strong>{product.title || product.handle}</strong>
                              <span>{product.handle}</span>
                            </span>
                            <span className="product-picker__option-action">
                              {selected ? "Selected" : "Select"}
                            </span>
                          </button>
                        );
                      })
                    : null}
                </div>
                {productPageInfo?.hasNextPage ? (
                  <Button
                    onClick={() =>
                      loadProductOptions({
                        append: true,
                        cursor: productPageInfo.endCursor || "",
                        query: productSearchQuery,
                      })
                    }
                    loading={productLoadingMore}
                  >
                    Load more products
                  </Button>
                ) : null}
              </div>
            ) : null}
            <TextField
              label="Collection handles or IDs"
              value={collectionTargetInput}
              onChange={setCollectionTargetInput}
              helpText="Comma-separated collection handles or IDs."
              error={errors.collectionTargets}
              autoComplete="off"
            />
          </FormLayout>
        </BlockStack>
      </div>
    </BlockStack>
  );
}

function ContentStep({ timer, updateBuilder, errors = {} }) {
  return (
    <FormLayout>
      <TextField
        label="Eyebrow"
        value={timer.content.eyebrow}
        onChange={(value) => updateBuilder("content.eyebrow", value)}
        autoComplete="off"
      />
      <TextField
        label="Title"
        value={timer.content.title}
        onChange={(value) => updateBuilder("content.title", value)}
        error={errors.title}
        autoComplete="off"
      />
      <TextField
        label="Subtitle"
        value={timer.content.subtitle}
        onChange={(value) => updateBuilder("content.subtitle", value)}
        autoComplete="off"
      />
      <FormLayout.Group>
        <TextField
          label="Timer label"
          value={timer.content.timerLabel}
          onChange={(value) => updateBuilder("content.timerLabel", value)}
          autoComplete="off"
        />
        <TextField
          label="CTA label"
          value={timer.content.ctaLabel}
          onChange={(value) => updateBuilder("content.ctaLabel", value)}
          error={errors.ctaLabel}
          autoComplete="off"
        />
      </FormLayout.Group>
      <TextField
        label="CTA URL"
        value={timer.content.ctaUrl}
        onChange={(value) => updateBuilder("content.ctaUrl", value)}
        error={errors.ctaUrl}
        autoComplete="off"
      />
      <BlockStack gap="200">
        <Text as="p" tone="subdued">
          CTA behavior
        </Text>
        <RadioButton
          label="Hide CTA"
          checked={timer.content.ctaBehavior === "hidden"}
          id="hidden"
          name="ctaBehavior"
          onChange={() => updateBuilder("content.ctaBehavior", "hidden")}
        />
        <RadioButton
          label="Show CTA while timer is active"
          checked={timer.content.ctaBehavior === "visible"}
          id="visible"
          name="ctaBehavior"
          onChange={() => updateBuilder("content.ctaBehavior", "visible")}
        />
        <RadioButton
          label="Disable CTA when the timer expires"
          checked={timer.content.ctaBehavior === "disabled-after-expiry"}
          id="disabled-after-expiry"
          name="ctaBehavior"
          onChange={() => updateBuilder("content.ctaBehavior", "disabled-after-expiry")}
        />
      </BlockStack>
    </FormLayout>
  );
}

function DesignStep({ timer, updateBuilder, errors = {} }) {
  const setBlockShape = (value) => {
    updateBuilder("design.blockShape", value);
    if (value === "circle") updateBuilder("design.blockBorderRadius", 999);
    if (value === "square") updateBuilder("design.blockBorderRadius", 8);
  };

  return (
    <FormLayout>
      <div className="builder-section">
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Layout and time boxes
          </Text>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label="Content flow"
                helpText="Choose whether the text, countdown, and CTA stack vertically or sit in one balanced horizontal row."
                options={[
                  { label: "Vertical stack", value: "vertical" },
                  { label: "Horizontal row", value: "horizontal" },
                ]}
                value={timer.design.layout}
                onChange={(value) => updateBuilder("design.layout", value)}
                error={errors.layout}
              />
              <Select
                label="Countdown style"
                options={[
                  { label: "Labeled blocks", value: "blocks" },
                  { label: "Compact time", value: "compact" },
                ]}
                value={timer.design.displayStyle || "blocks"}
                onChange={(value) => updateBuilder("design.displayStyle", value)}
                error={errors.displayStyle}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label="Time box shape"
                options={[
                  { label: "Soft square", value: "square" },
                  { label: "Circle", value: "circle" },
                  { label: "Custom radius", value: "custom" },
                ]}
                value={timer.design.blockShape || "square"}
                onChange={setBlockShape}
                error={errors.blockShape}
              />
              <TextField
                label="Time box border radius"
                type="number"
                value={String(timer.design.blockBorderRadius ?? timer.design.borderRadius ?? 8)}
                onChange={(value) => {
                  const radius = Number(value);
                  updateBuilder("design.blockShape", radius >= 999 ? "circle" : "custom");
                  updateBuilder("design.blockBorderRadius", radius);
                }}
                helpText="Use 999 for circular boxes, 0 for sharp corners, or any value in between."
                error={errors.blockBorderRadius}
                autoComplete="off"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <ColorField
                label="Time box background"
                value={isValidHexColor(timer.design.timeBoxBackgroundColor) ? timer.design.timeBoxBackgroundColor : "#000000"}
                pickerLabel="Time box background color picker"
                onChange={(value) => updateBuilder("design.timeBoxBackgroundColor", value)}
              />
              <ColorField
                label="Time box text"
                value={timer.design.timeBoxTextColor}
                pickerLabel="Time box text color picker"
                error={errors.timeBoxTextColor}
                onChange={(value) => updateBuilder("design.timeBoxTextColor", value)}
              />
              <ColorField
                label="Time box border"
                value={timer.design.timeBoxBorderColor}
                pickerLabel="Time box border color picker"
                error={errors.timeBoxBorderColor}
                onChange={(value) => updateBuilder("design.timeBoxBorderColor", value)}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label="Time box border style"
                options={[
                  { label: "No border", value: "none" },
                  { label: "Solid", value: "solid" },
                  { label: "Dashed", value: "dashed" },
                ]}
                value={timer.design.timeBoxBorderStyle || "none"}
                onChange={(value) => updateBuilder("design.timeBoxBorderStyle", value)}
                error={errors.timeBoxBorderStyle}
              />
              <TextField
                label="Time box border width"
                type="number"
                value={String(timer.design.timeBoxBorderWidth ?? 0)}
                onChange={(value) => updateBuilder("design.timeBoxBorderWidth", Number(value))}
                error={errors.timeBoxBorderWidth}
                autoComplete="off"
              />
              <TextField
                label="Flag side tail length"
                type="number"
                value={String(timer.design.flagSideTailLength ?? 0)}
                onChange={(value) => updateBuilder("design.flagSideTailLength", Number(value))}
                error={errors.flagSideTailLength}
                helpText="Extends the left and right border lines downward on product-card flags."
                autoComplete="off"
              />
            </FormLayout.Group>
            <BlockStack gap="200">
              <Text as="p" tone="subdued">
                Visible countdown boxes
              </Text>
              <InlineStack gap="400">
                <Checkbox
                  label="Show days box"
                  checked={timer.design.showDays !== false}
                  onChange={(value) => updateBuilder("design.showDays", value)}
                />
                <Checkbox
                  label="Show hours box"
                  checked={timer.design.showHours !== false}
                  onChange={(value) => updateBuilder("design.showHours", value)}
                />
                <Checkbox
                  label="Show minutes box"
                  checked={timer.design.showMinutes !== false}
                  onChange={(value) => updateBuilder("design.showMinutes", value)}
                />
                <Checkbox
                  label="Show seconds box"
                  checked={timer.design.showSeconds !== false}
                  onChange={(value) => updateBuilder("design.showSeconds", value)}
                />
              </InlineStack>
              <Checkbox
                label="Show time labels"
                checked={timer.design.showTimeLabels !== false}
                onChange={(value) => updateBuilder("design.showTimeLabels", value)}
              />
              {errors.timeUnits ? (
                <Text tone="critical" as="p">
                  {errors.timeUnits}
                </Text>
              ) : null}
            </BlockStack>
          </FormLayout>
        </BlockStack>
      </div>
      <div className="builder-section">
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Colors
          </Text>
      <FormLayout.Group>
        <Select
          label="Background mode"
          options={[
            { label: "Solid color", value: "solid" },
            { label: "Gradient", value: "gradient" },
          ]}
          value={timer.design.backgroundMode || "solid"}
          onChange={(value) => updateBuilder("design.backgroundMode", value)}
          error={errors.backgroundMode}
        />
        <TextField
          label="Gradient angle"
          type="number"
          value={String(timer.design.gradientAngle ?? 90)}
          onChange={(value) => updateBuilder("design.gradientAngle", Number(value))}
          helpText="Used when background mode is Gradient."
          error={errors.gradientAngle}
          autoComplete="off"
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <ColorField
          label="Gradient start"
          value={timer.design.gradientStartColor}
          pickerLabel="Gradient start color picker"
          error={errors.gradientStartColor}
          onChange={(value) => updateBuilder("design.gradientStartColor", value)}
        />
        <ColorField
          label="Gradient end"
          value={timer.design.gradientEndColor}
          pickerLabel="Gradient end color picker"
          error={errors.gradientEndColor}
          onChange={(value) => updateBuilder("design.gradientEndColor", value)}
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <ColorField
          label="Background color"
          value={timer.design.backgroundColor}
          pickerLabel="Background color picker"
          error={errors.backgroundColor}
          onChange={(value) => updateBuilder("design.backgroundColor", value)}
        />
        <ColorField
          label="Text color"
          value={timer.design.textColor}
          pickerLabel="Text color picker"
          error={errors.textColor}
          onChange={(value) => updateBuilder("design.textColor", value)}
        />
        <ColorField
          label="Accent color"
          value={timer.design.accentColor}
          pickerLabel="Accent color picker"
          error={errors.accentColor}
          onChange={(value) => updateBuilder("design.accentColor", value)}
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <ColorField
          label="Eyebrow color"
          value={timer.design.eyebrowColor}
          pickerLabel="Eyebrow color picker"
          error={errors.eyebrowColor}
          onChange={(value) => updateBuilder("design.eyebrowColor", value)}
        />
        <ColorField
          label="Title color"
          value={timer.design.titleColor}
          pickerLabel="Title color picker"
          error={errors.titleColor}
          onChange={(value) => updateBuilder("design.titleColor", value)}
        />
        <ColorField
          label="Subtitle color"
          value={timer.design.subtitleColor}
          pickerLabel="Subtitle color picker"
          error={errors.subtitleColor}
          onChange={(value) => updateBuilder("design.subtitleColor", value)}
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <ColorField
          label="Countdown color"
          value={timer.design.timerColor}
          pickerLabel="Countdown color picker"
          error={errors.timerColor}
          onChange={(value) => updateBuilder("design.timerColor", value)}
        />
        <ColorField
          label="Button background"
          value={timer.design.ctaBackgroundColor}
          pickerLabel="Button background color picker"
          error={errors.ctaBackgroundColor}
          onChange={(value) => updateBuilder("design.ctaBackgroundColor", value)}
        />
        <ColorField
          label="Button text color"
          value={timer.design.ctaTextColor}
          pickerLabel="Button text color picker"
          error={errors.ctaTextColor}
          onChange={(value) => updateBuilder("design.ctaTextColor", value)}
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <ColorField
          label="Powered by background"
          value={timer.design.brandingBackgroundColor}
          pickerLabel="Powered by background color picker"
          error={errors.brandingBackgroundColor}
          onChange={(value) => updateBuilder("design.brandingBackgroundColor", value)}
        />
        <ColorField
          label="Powered by text"
          value={timer.design.brandingTextColor}
          pickerLabel="Powered by text color picker"
          error={errors.brandingTextColor}
          onChange={(value) => updateBuilder("design.brandingTextColor", value)}
        />
      </FormLayout.Group>
        </BlockStack>
      </div>
      <div className="builder-section">
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Typography and spacing
          </Text>
      <FormLayout.Group>
        <TextField
          label="Base font size"
          type="number"
          value={String(timer.design.fontSize)}
          onChange={(value) => updateBuilder("design.fontSize", Number(value))}
          error={errors.fontSize}
          autoComplete="off"
        />
        <TextField
          label="Eyebrow font size"
          type="number"
          value={String(timer.design.eyebrowFontSize)}
          onChange={(value) => updateBuilder("design.eyebrowFontSize", Number(value))}
          error={errors.eyebrowFontSize}
          autoComplete="off"
        />
        <TextField
          label="Title font size"
          type="number"
          value={String(timer.design.titleFontSize)}
          onChange={(value) => updateBuilder("design.titleFontSize", Number(value))}
          error={errors.titleFontSize}
          autoComplete="off"
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <TextField
          label="Subtitle font size"
          type="number"
          value={String(timer.design.subtitleFontSize)}
          onChange={(value) => updateBuilder("design.subtitleFontSize", Number(value))}
          error={errors.subtitleFontSize}
          autoComplete="off"
        />
        <TextField
          label="Countdown font size"
          type="number"
          value={String(timer.design.timerFontSize)}
          onChange={(value) => updateBuilder("design.timerFontSize", Number(value))}
          error={errors.timerFontSize}
          autoComplete="off"
        />
        <TextField
          label="Button font size"
          type="number"
          value={String(timer.design.ctaFontSize)}
          onChange={(value) => updateBuilder("design.ctaFontSize", Number(value))}
          error={errors.ctaFontSize}
          autoComplete="off"
        />
      </FormLayout.Group>
      <FormLayout.Group>
        <TextField
          label="Padding top"
          type="number"
          value={String(timer.design.paddingTop)}
          onChange={(value) => updateBuilder("design.paddingTop", Number(value))}
          autoComplete="off"
        />
        <TextField
          label="Padding bottom"
          type="number"
          value={String(timer.design.paddingBottom)}
          onChange={(value) => updateBuilder("design.paddingBottom", Number(value))}
          autoComplete="off"
        />
        <TextField
          label="Border radius"
          type="number"
          value={String(timer.design.borderRadius)}
          onChange={(value) => updateBuilder("design.borderRadius", Number(value))}
          autoComplete="off"
        />
      </FormLayout.Group>
        </BlockStack>
      </div>
      <TextField
        label="Custom CSS"
        value={timer.design.customCss}
        onChange={(value) => updateBuilder("design.customCss", value)}
        multiline={5}
        monospaced
        autoComplete="off"
      />
    </FormLayout>
  );
}

function ColorField({ label, value, pickerLabel, onChange, error = null }) {
  const safeValue = /^#[0-9a-f]{6}$/i.test(value || "") ? value : "#000000";
  return (
    <div className="color-field">
      <label className="color-field__label">{label}</label>
      <div className="color-field__controls">
        <input
          aria-label={pickerLabel}
          className="color-field__picker"
          type="color"
          value={safeValue}
          onChange={(event) => onChange(event.target.value)}
        />
        <TextField
          label={label}
          labelHidden
          value={value || ""}
          onChange={onChange}
          prefix={colorSwatch(value)}
          error={error}
          autoComplete="off"
        />
      </div>
    </div>
  );
}

function PreviewPanel({ timer, hideBranding, selectedTemplate }) {
  const template = selectedTemplate || templateById(timer.templateId);
  const stripPreview = (timer.placementTargets || []).some((placement) =>
    ["top", "bottom", "sticky-top", "sticky-bottom"].includes(placement)
  );
  const presentation = template ? templatePreviewPresentation(template) : stripPreview ? "strip" : "default";

  return (
    <Card>
      <div className="builder-preview-panel">
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Live preview
            </Text>
            <Badge tone={timer.templateId ? "success" : "info"}>
              {timer.templateId ? "Selected" : "Preview"}
            </Badge>
          </InlineStack>
          <div className="preview-device-stack">
            <div className="preview-device preview-device--desktop">
              <Text variant="headingSm" as="p">
                Desktop
              </Text>
              <div className={stripPreview ? "preview-device__desktop-frame" : undefined}>
                <TimerPreview
                  timerEnabled={timer.timerEnabled}
                  timerEndTime={timer.endAt}
                  content={timer.content}
                  design={timer.design}
                  hideBranding={hideBranding}
                  demoDurationSeconds={Number(timer.durationMinutes || 90) * 60}
                  presentation={presentation}
                />
              </div>
            </div>
            <div className="preview-device preview-device--mobile">
              <Text variant="headingSm" as="p">
                Mobile
              </Text>
              <div className="preview-device__phone">
                <TimerPreview
                  timerEnabled={timer.timerEnabled}
                  timerEndTime={timer.endAt}
                  content={timer.content}
                  design={timer.design}
                  hideBranding={hideBranding}
                  demoDurationSeconds={Number(timer.durationMinutes || 90) * 60}
                  presentation={presentation}
                  viewport="mobile"
                />
              </div>
            </div>
          </div>
          <BlockStack gap="100">
            <Text variant="headingSm" as="p">
              {selectedTemplate?.name || timer.name || "Custom timer"}
            </Text>
            <Text tone="subdued" as="p">
              {pageTargetLabel(timer.pageTarget)} ·{" "}
              {(timer.placementTargets || []).map(placementLabel).join(", ")}
            </Text>
            <Text tone="subdued" as="p">
              {timerScheduleSummary(timer)}
            </Text>
          </BlockStack>
        </BlockStack>
      </div>
    </Card>
  );
}
